import {
  GatewayContact,
  GatewayEntreprise,
  GatewayExportDocuments,
} from "@conformite/gateway";
import {
  ComplianceProgressBar,
  Dropdown,
  TitleWithNumber,
  SidePane,
  Button,
} from "@endya-conformite/ui-kit";
import { AdhesionDataEntreprisesItem } from "@src/adhesion/AdhesionData/adhesionDataEntreprises";
import { GatewayEntrepriseApi } from "@src/api/entreprise2.api";
import { handleGatewayError } from "@src/api/utils/handleGatewayError";
import { ExportFilesButton } from "@src/components/ExportFilesButton/ExportFilesButton";
import { MainContentPageTemplate } from "@src/components/MainPageTemplate/MainContentPageTemplate";
import { useIsAllowed } from "@src/session/useIsAllowed";
import { useEntreprises } from "@src/store/store.entreprise";
import { useMillesime } from "@src/store/store.millesime";
import { StoreEntryFetchStatus } from "@src/store/store";
import { useCallback, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDownloadAttestation } from "./attestation/useDownloadAttestation";
import { ExportEntrepriseButton } from "./export/ExportEntrepriseButton";
import { ParcoursEntreprise } from "./parcours/ParcoursEntreprise";
import { EntrepriseActions } from "./SocieteAction";
import { EntrepriseComponent, SocieteList } from "./SocieteList";
import { useEntrepriseAvancement } from "./useEntrepriseAvancement";
import { useSocieteFiltered } from "./useSocieteFiltered";

type SocieteProps = {
  entreprises?: GatewayEntreprise.EntrepriseAControllerResponseType[];
  isLoading: boolean;
  onAddEntreprise: (
    handleClose: () => void
  ) => (data: AdhesionDataEntreprisesItem) => void;
  onDeleteEntreprises: (entreprisesIds: string[]) => void;
  millesime: number;
};

function Societes({
  entreprises,
  isLoading,
  onAddEntreprise,
  onDeleteEntreprises,
}: SocieteProps) {
  const { hasAllowedRole } = useIsAllowed();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const unmountAddSocieteKey = useRef(0);
  const closeDialog = useCallback(() => {
    setIsOpen(false);
    unmountAddSocieteKey.current += 1;
  }, []);
  const { getEntrepriseAvancement } = useEntrepriseAvancement();
  const SocieteAvancement = useCallback<EntrepriseComponent>(
    ({ entreprise }) => (
      <ComplianceProgressBar progress={getEntrepriseAvancement(entreprise)} />
    ),
    [getEntrepriseAvancement]
  );

  const { entreprisesBySiren } = useEntreprises();
  const { getIsAllowedToDownloadAttestation } = useDownloadAttestation();
  const entreprisesToDownload = useMemo(
    () => (entreprisesBySiren ? Object.values(entreprisesBySiren) : []),
    [entreprisesBySiren]
  );
  const props = useSocieteFiltered(entreprises ?? [], undefined, pathname);

  return (
    <>
      <SocieteList
        {...props}
        isLoading={isLoading}
        SocieteConformite={SocieteAvancement}
        adminActions={
          hasAllowedRole(
            GatewayContact.ContactRoleUtilisateur.ADMINISTRATEUR
          ) && (
            <Dropdown content="Actions" placement="bottom-end">
              {({ close }) => (
                <>
                  <Button
                    variant="text"
                    onClick={() => {
                      setIsOpen(true);
                      close();
                    }}
                  >
                    Ajouter une société
                  </Button>
                  {entreprisesToDownload && (
                    <ExportEntrepriseButton
                      variant="text"
                      entreprises={entreprisesToDownload}
                    >
                      Télécharger la liste des sociétés connues
                    </ExportEntrepriseButton>
                  )}
                  {entreprises?.some(getIsAllowedToDownloadAttestation) && (
                    <ExportFilesButton
                      type={GatewayExportDocuments.ExportDocumentType.ADHESION}
                    />
                  )}
                </>
              )}
            </Dropdown>
          )
        }
        SocieteActions={EntrepriseActions}
        actionOnClick={(entreprise) => {
          navigate(`/societes/${entreprise.siren}`);
        }}
        onDeleteEntreprises={onDeleteEntreprises}
      />
      <SidePane isOpen={isOpen} setIsOpen={setIsOpen}>
        {() => (
          <ParcoursEntreprise
            key={unmountAddSocieteKey.current}
            onSubmit={onAddEntreprise(closeDialog)}
            withPayment
          />
        )}
      </SidePane>
    </>
  );
}

export function SocietesContainer() {
  const { entreprisesBySiren, setEntreprises, setEntreprise, status } =
    useEntreprises();
  const { selected } = useMillesime();
  const { hasAllowedRole } = useIsAllowed();

  const entreprisesArray = useMemo(
    () => Object.values(entreprisesBySiren ?? {}),
    [entreprisesBySiren]
  );

  const handleAddEntreprise = useCallback(
    (handleClose: () => void) => (data: AdhesionDataEntreprisesItem) => {
      if (!selected) throw Error("No millesime selected");
      handleClose();
      GatewayEntrepriseApi.getBySiren(data.siren as string, selected)
        .then((newEntreprise) => {
          setEntreprise(newEntreprise);
        })
        .catch(() =>
          handleGatewayError({
            onUnhandled: () =>
              "Une erreur est survenue lors de la récupération de la nouvelle entreprise",
          })
        );
    },
    [selected, setEntreprise]
  );

  const handleDeleteEntreprise = useCallback(
    (deletedIds: string[]) => {
      setEntreprises(
        entreprisesArray.filter(
          (entreprise) => !deletedIds.includes(entreprise.hubspotId ?? "")
        )
      );
    },
    [setEntreprises, entreprisesArray]
  );

  return (
    <MainContentPageTemplate
      hasMillesimeNavigation={hasAllowedRole(
        GatewayContact.ContactRoleUtilisateur.ADMINISTRATEUR,
        GatewayContact.ContactRoleUtilisateur.RESPONSABLE_CONFORMITE
      )}
      title={
        <TitleWithNumber
          title="Conformité des sociétés"
          number={entreprisesArray.length}
        />
      }
    >
      {selected && (
        <Societes
          millesime={selected}
          entreprises={entreprisesArray}
          isLoading={status !== StoreEntryFetchStatus.Loaded}
          onAddEntreprise={handleAddEntreprise}
          onDeleteEntreprises={handleDeleteEntreprise}
        />
      )}
    </MainContentPageTemplate>
  );
}
